import { IZone, IFacility } from '../custom/classes/production';
import { IPackageListingAppState } from '../redux-state';
import { flatMap } from 'lodash';
import { filterMap, filterMapByValue } from './map-helpers';

export interface ICartItemDetail {
    facility: IFacility;
    itemTitle: string;
    itemDate: string;
    itemTime: string;
    itemIdToRemove: number;
}

export function getCartItemDetails(state: IPackageListingAppState, itemId: number): ICartItemDetail {
    let itemDetail: ICartItemDetail = null;

    if (state.packageListingSettings.isFixed) {
        const matchedFixed = state.packageListing.packages.filter(p => p.id == itemId);

        if (matchedFixed.length === 1) {
            itemDetail = {
                facility: matchedFixed[0].facility,
                itemDate: "",
                itemTime: "",
                itemTitle: matchedFixed[0].title,
                itemIdToRemove: itemId
            };
        } else {
            const allSubPackages = flatMap(state.packageListing.packages
                .map(pkg => pkg.subPackages));

            const matchedSubPackage = allSubPackages.filter(p => p.id == itemId)[0];

            if (matchedSubPackage) {
                var packageId = filterMapByValue(state.packageListing.subPackagesInCart,
                    value => {
                        return value.indexOf(matchedSubPackage.id) >= 0
                    }).keys().next().value;

                if (packageId) {
                    const subPackages = state.packageListing.subPackagesInCart.get(packageId);
                    itemDetail = {
                        facility: matchedSubPackage.facility,
                        itemDate: "",
                        itemTime: "",
                        itemIdToRemove: packageId,
                        itemTitle: subPackages.indexOf(matchedSubPackage.id) === 0 ?
                            state.packageListing.packages.filter(p => p.id === packageId)[0].title
                            : ""
                    };
                }

            }

        }
    } else {
        const allPerf = flatMap(state.packageListing.packageDetail.performanceGroupDetails
            .map(group => flatMap(group.appProductions.map(prod => prod.performances))));

        const matchedPerf = allPerf.filter(p => p.performanceId == itemId)[0];

        if (matchedPerf) {
            itemDetail = {
                facility: matchedPerf.facility,
                itemDate: matchedPerf.performanceDateDayString,
                itemTime: matchedPerf.performanceTimeString,
                itemTitle: matchedPerf.title,
                itemIdToRemove: itemId
            };

        }
    }

    return itemDetail;
}

export function isMixedPackage(state: IPackageListingAppState): boolean {
    return !state.packageListingSettings.isFixed && state.packageListing.generalAdmissionPerfIds.length > 0
}

export function isMixedReservation(state: IPackageListingAppState): boolean {
    if (!state.packageListingSettings.isFixed && state.packageListing.cartItems.size > 0
        && state.packageListing.generalAdmissionPerfIds.length > 0) {
        let cartItemIds = Array.from(state.packageListing.cartItems.keys());
        return cartItemIds.some(id => state.packageListing.generalAdmissionPerfIds.includes(id));
    }

    return false;
}

export function isGeneralAdmissionPerformance(state: IPackageListingAppState, itemId: number): boolean {
    if (!state.packageListingSettings.isFixed && state.packageListing.generalAdmissionPerfIds.length > 0) {
        return state.packageListing.generalAdmissionPerfIds.some(id => id == itemId);
    }

    return false;
}

export function getGAPerfWithMissingZoneCount(state: IPackageListingAppState, cartPerfItems: Map<number, number>): number {
    let genAdmissionPerfCount = 0;
    const cartItems = Array.from(cartPerfItems);
    cartItems.forEach(item => {
        if (isGeneralAdmissionPerformance(state, item[0]) && item[1] == 0) {
            genAdmissionPerfCount++;
        }
    });

    return genAdmissionPerfCount;
}

export function isPricingSelected(state: IPackageListingAppState): boolean {
    return getGAPerfWithMissingZoneCount(state, state.packageListing.cartItems) == 0;
}

// AAC26-75 - PWYC helper functions

export function isPwycPerformance(state: IPackageListingAppState, perfId: number): boolean {
    return state.packageListing.pwycPerformanceSettings != null
        && state.packageListing.pwycPerformanceSettings[perfId] != null
        && state.packageListing.pwycPerformanceSettings[perfId].usePayWhatYouCan;
}

export function isPwycZone(state: IPackageListingAppState, perfId: number, zoneId: number, priceTypeIds?: number[]): boolean {
    if (!isPwycPerformance(state, perfId)) return false;
    const settings = state.packageListing.pwycPerformanceSettings[perfId];
    // Empty list means all zones/price types are eligible
    const zoneEligible = settings.pwycZoneIds.length === 0 || settings.pwycZoneIds.indexOf(zoneId) >= 0;
    // When both lists are specified, zone must also have at least one eligible price type
    const priceTypeEligible = settings.pwycPriceTypeIds.length === 0
        || !priceTypeIds || priceTypeIds.length === 0
        || priceTypeIds.some(ptId => settings.pwycPriceTypeIds.indexOf(ptId) >= 0);
    return zoneEligible && priceTypeEligible;
}

export function isPwycPriceType(state: IPackageListingAppState, perfId: number, priceTypeId: number, zoneId?: number): boolean {
    if (!isPwycPerformance(state, perfId)) return false;
    const settings = state.packageListing.pwycPerformanceSettings[perfId];
    // Empty list means all price types/zones are eligible
    const priceTypeEligible = settings.pwycPriceTypeIds.length === 0 || settings.pwycPriceTypeIds.indexOf(priceTypeId) >= 0;
    // When both lists are specified, price type must also be in an eligible zone
    const zoneEligible = settings.pwycZoneIds.length === 0
        || zoneId == null
        || settings.pwycZoneIds.indexOf(zoneId) >= 0;
    return priceTypeEligible && zoneEligible;
}

export function getPwycMinAmount(state: IPackageListingAppState, perfId: number): number {
    if (!isPwycPerformance(state, perfId)) return 0;
    return state.packageListing.pwycPerformanceSettings[perfId].pwycMinAmount;
}

export function getPwycAmount(state: IPackageListingAppState, perfId: number, priceTypeId: number): number | null {
    const key = `${perfId}_${priceTypeId}`;
    return state.packageListing.pwycAmounts.has(key) ? state.packageListing.pwycAmounts.get(key) : null;
}